import { create } from 'zustand';
import {
  payMembershipOrder,
  payPointOrder,
  fetchMembershipOrders,
  fetchPointOrders,
  fetchMembershipOrderPaymentStatus,
  fetchPointOrderPaymentStatus,
} from '@/service/api/membership';

export type PayChannel = 'WECHAT' | 'ALIPAY';
export type OrderType = 'membership' | 'point';

export interface PaymentData {
  amount: string | number;
  orderId: string | number;
  orderNo: string;
  payUrl: string;
  expiresTime: string;
  orderType: OrderType;
}

export interface PaymentRequest {
  orderId: string | number;
  orderType: OrderType;
  amount?: string | number;
}

interface PayState {
  payModalVisible: boolean;
  payType: PayChannel;
  qrCodeLoading: boolean;
  paymentData: PaymentData;
  paymentRequest: PaymentRequest | null;
  paymentStatus: string | null;

  // 方法
  openPayModal: (data: Partial<PaymentData>) => void;
  closePayModal: () => void;
  setPayType: (channel: PayChannel) => void;
  createPaymentAndOpenModal: (
    orderId: string | number,
    orderType?: OrderType,
    channel?: PayChannel,
    amount?: string | number
  ) => Promise<Api.Membership.PaymentResponse | null>;
  fetchPaymentQRCode: (
    orderId: string | number,
    orderType: OrderType,
    channel: PayChannel
  ) => Promise<Api.Membership.PaymentResponse | null>;
  getPaymentStatus: (
    orderId: string | number,
    orderType: OrderType,
    orderNo?: string
  ) => Promise<Api.Membership.PaymentStatusResponse | null>;
  resetPayStore: () => void;
}

const initialPaymentData: PaymentData = {
  amount: '0.00',
  orderId: '',
  orderNo: '',
  payUrl: '',
  expiresTime: '',
  orderType: 'membership',
};

export const usePayStore = create<PayState>((set, get) => ({
  payModalVisible: false,
  payType: 'WECHAT',
  qrCodeLoading: false,
  paymentData: { ...initialPaymentData },
  paymentRequest: null,
  paymentStatus: null,

  openPayModal: (data) => {
    set((state) => ({
      payModalVisible: true,
      paymentData: { ...state.paymentData, ...data },
    }));
  },

  closePayModal: () => {
    set({
      payModalVisible: false,
    });
  },

  setPayType: (channel) => {
    set({ payType: channel });
  },

  /**
   * 发起订单支付并获取二维码信息
   */
  fetchPaymentQRCode: async (orderId, orderType, channel) => {
    set({ qrCodeLoading: true });
    try {
      let response: Api.Membership.PaymentResponse | null = null;
      if (orderType === 'membership') {
        response = await payMembershipOrder(orderId, { payment_channel: channel });
      } else {
        response = await payPointOrder(orderId, { payment_channel: channel });
      }

      if (response) {
        set((state) => ({
          paymentData: {
            ...state.paymentData,
            amount: response?.amount || state.paymentData.amount,
            orderId: response?.order_id || orderId,
            orderNo: response?.order_no || state.paymentData.orderNo,
            payUrl: response?.qr_code_url || '',
            expiresTime: response?.expires_at || '',
            orderType,
          },
        }));
      }
      return response;
    } catch (error) {
      console.error('获取支付二维码失败:', error);
      return null;
    } finally {
      set({ qrCodeLoading: false });
    }
  },

  /**
   * 创建/刷新支付并打开弹窗
   */
  createPaymentAndOpenModal: async (
    orderId,
    orderType = 'membership',
    channel = 'WECHAT',
    amount
  ) => {
    set({
      payType: channel,
      paymentRequest: { orderId, orderType, amount },
    });

    if (amount !== undefined) {
      set((state) => ({
        paymentData: { ...state.paymentData, amount, orderId, orderType },
      }));
    }

    const res = await get().fetchPaymentQRCode(orderId, orderType, channel);
    set({ payModalVisible: true });
    return res;
  },

  /**
   * 查询订单支付状态
   */
  getPaymentStatus: async (orderId, orderType) => {
    try {
      let res: Api.Membership.PaymentStatusResponse | null = null;
      if (orderType === 'membership') {
        res = await fetchMembershipOrderPaymentStatus(orderId);
      } else {
        res = await fetchPointOrderPaymentStatus(orderId);
      }

      if (res) {
        set({ paymentStatus: res.payment_status || (res.paid ? 'SUCCEEDED' : null) });
        return res;
      }
      return null;
    } catch (error) {
      console.error('查询订单支付状态失败:', error);
      return null;
    }
  },

  resetPayStore: () => {
    set({
      payModalVisible: false,
      payType: 'WECHAT',
      qrCodeLoading: false,
      paymentData: { ...initialPaymentData },
      paymentRequest: null,
      paymentStatus: null,
    });
  },
}));
